import { supabase } from '@/lib/supabase'

/**
 * Team-chat message search (CP1 backend — data helper only, no UI).
 *
 * Runs a plain RLS-relying query against team_messages: substring match on
 * content via ILIKE, backed by the team_messages_content_search trigram GIN
 * index. There is NO RPC and NO SECURITY DEFINER — the query inherits the
 * existing team_messages_select policy (membership via
 * team_conversation_participants), so a caller can only ever match messages
 * in channels they participate in. Search is not a data-leak surface.
 *
 * CP2 wires the UI (search box + results + deep-link to the channel).
 */

export interface TeamMessageSearchResult {
  id: string
  team_conversation_id: string
  user_id: string | null
  content: string
  created_at: string
  /** Author display name, via team_messages_user_fkey -> profiles. */
  sender_name: string | null
}

/** Max rows returned per search. */
const SEARCH_LIMIT = 50

/**
 * Escape LIKE/ILIKE wildcards in user input so a literal `%` or `_` is matched
 * as itself rather than acting as a wildcard. Postgres LIKE uses backslash as
 * the default escape character, so `\%`, `\_`, and `\\` are honored without an
 * explicit ESCAPE clause. Single pass over the original string — no
 * double-escaping.
 */
function escapeLike(input: string): string {
  return input.replace(/[\\%_]/g, (c) => `\\${c}`)
}

/**
 * Search the caller's team-chat messages for `query` (case-insensitive
 * substring). Returns up to 50 most-recent matches, newest first. Soft-deleted
 * messages (deleted_at set) are excluded.
 *
 * Empty/whitespace-only queries short-circuit to [] — no '%%' full scan.
 */
export async function searchTeamMessages(
  query: string,
): Promise<TeamMessageSearchResult[]> {
  const trimmed = query.trim()
  if (!trimmed) return []

  const pattern = `%${escapeLike(trimmed)}%`

  const { data, error } = await supabase
    .from('team_messages')
    .select(
      'id, team_conversation_id, user_id, content, created_at, profiles!team_messages_user_fkey(full_name)',
    )
    .ilike('content', pattern)
    .is('deleted_at', null)
    .order('created_at', { ascending: false })
    .limit(SEARCH_LIMIT)

  if (error) {
    console.error('searchTeamMessages failed', error)
    throw error
  }

  return (data ?? []).map((row: any) => ({
    id: row.id,
    team_conversation_id: row.team_conversation_id,
    user_id: row.user_id,
    content: row.content,
    created_at: row.created_at,
    sender_name: row.profiles?.full_name ?? null,
  }))
}
